// 2026-05-26 - FormuLearning - Step 5 카드 컨테이너
// 2026-09-23 수정 - 이슈 #83: DoE 분석 요청 동안 진행 중 작업 잠금(`busyStore`) 등록 — Break 없음.
// 파일 위치: frontend/src/features/step5/Step5Card.tsx
// 2026-09-23 수정 - 이슈 #85: 생성·분석 실패(422 사유)를 알림으로 보인다 · Mixture 합계 입력 상태(저장 케이스의
//                   행 합에서 복원) · 생성 불가 조건(방식 미선택·요인 < 2·BBD 요인 < 3)은 버튼을 막고 사유를 보인다.
// 2026-11-02 수정 - 이슈 #113: 저장된 Factor 가 비어 있으면 Step 3 X 변수 min/max 로 화면 사전 채움(저장 전, C-08).

import { useEffect, useState, useCallback } from 'react'
import { useProjectStore } from '../../stores/projectStore'
import { toast } from '../../stores/toastStore'
import { useBusyStore } from '../../stores/busyStore'
import { apiErrorMessage } from '../../lib/apiError'
import { getStep5, updateStep5, generateCases, analyzeDoe, downloadTemplateUrl, uploadYValues } from '../../api/step5'
import type { Step5Data, Factor, DoeResponse, DoECase } from '../../api/step5'
import { getStep3 } from '../../api/step3'
import type { XVariable } from '../../api/step3'
const EMPTY_ANALYSIS = { anova: [], r_squared: null, q_squared: null }
const EMPTY_GRAPH = { contour: [], response_surface: [], pareto: [], main_effect: [], interaction: [] }

function normalizeStep5(d: Step5Data): Step5Data {
  return {
    ...d,
    factors: d.factors ?? [],
    responses: d.responses ?? [],
    cases: d.cases ?? [],
    analysis_result: { ...EMPTY_ANALYSIS, ...d.analysis_result, anova: d.analysis_result?.anova ?? [] },
    graph_data: { ...EMPTY_GRAPH, ...d.graph_data },
  }
}

import DoeSelectSection from './DoeSelectSection'
import VariableInputSection from './VariableInputSection'
import CaseTableSection from './CaseTableSection'
import AnalysisSection from './AnalysisSection'

const DEFAULT_MIXTURE_TOTAL = 100

// 2026-11-02 - 이슈 #113(`M1016-06`, `Q-260525-03` 잠정): Step 3 X 변수 → Step 5 Factor 사전 채움(C-08).
// 범위가 있는 행(selected·selected(non-weight), min < max)만 가져온다 — fixed 는 범위가 없어 요인이 될 수 없고
// (BR-S5-01 min < max), balance 는 나머지로 정해지는 종속 성분이다. 범위는 Step 3 min/max 를 그대로 쓴다.
const PREFILL_MODES: ReadonlyArray<XVariable['mode']> = ['selected', 'selected(non-weight)']

function factorsFromStep3(xs: XVariable[]): Factor[] {
  return [...xs]
    .sort((a, b) => a.sort_order - b.sort_order)
    .filter((x) => PREFILL_MODES.includes(x.mode) && x.excipient.trim() && x.min_pct < x.max_pct)
    .map((x) => ({ name: x.excipient.trim(), min: x.min_pct, max: x.max_pct }))
}

// Mixture 케이스는 모든 행의 성분 합이 합계와 같다 — 합계를 따로 저장하지 않고 케이스에서 되읽는다
function mixtureTotalOf(d: Step5Data): number {
  if (d.doe_type !== 'mixture' || !d.cases.length) return DEFAULT_MIXTURE_TOTAL
  const sum = Object.values(d.cases[0].factors).reduce((a, v) => a + Number(v), 0)
  return Math.round(sum * 1000) / 1000
}

function generateBlockReason(d: Step5Data): string | null {
  if (!d.doe_type) return 'DoE 방식을 먼저 선택하세요.'
  const n = d.factors.length
  if (n < 2) return `요인이 2개 이상이어야 합니다 (현재 ${n}개).`
  if (d.doe_type === 'box_behnken' && n < 3) return `Box-Behnken 은 요인이 3개 이상이어야 합니다 (현재 ${n}개).`
  return null
}

export default function Step5Card() {
  const projectId = useProjectStore((s) => s.currentProject?.id)
  const [data, setData] = useState<Step5Data | null>(null)
  const [generating, setGenerating] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [uploadingY, setUploadingY] = useState(false)
  const [mixtureTotal, setMixtureTotal] = useState(DEFAULT_MIXTURE_TOTAL)
  // 저장 전 사전 채움값. 저장된 factors 가 비어 있을 때만 화면 초기값으로 쓰고 서버에는 쓰지 않는다 —
  // 사용자가 Factor 를 고치거나 케이스를 생성할 때 그 목록으로 저장된다(자동 쓰기 없음). 고치면 해제한다.
  const [prefill, setPrefill] = useState<Factor[] | null>(null)

  useEffect(() => {
    if (!projectId) return
    let alive = true
    Promise.all([getStep5(projectId), getStep3(projectId).catch(() => null)]).then(([d, s3]) => {
      if (!alive) return
      const n = normalizeStep5(d)
      const pre = !n.factors.length && s3 ? factorsFromStep3(s3.x_variables ?? []) : []
      setPrefill(pre.length ? pre : null)
      setData(pre.length ? { ...n, factors: pre } : n)
      setMixtureTotal(mixtureTotalOf(n))
    })
    return () => { alive = false }
  }, [projectId])

  // 서버 응답으로 상태를 바꿀 때 사전 채움이 살아 있으면(저장된 factors 가 아직 비어 있으면) 화면값을 유지한다
  const applyServer = useCallback((d: Step5Data) => {
    const n = normalizeStep5(d)
    setData(prefill && !n.factors.length ? { ...n, factors: prefill } : n)
  }, [prefill])

  const handleDoeTypeChange = useCallback(async (t: 'ccd' | 'box_behnken' | 'mixture') => {
    if (!projectId) return
    const updated = await updateStep5(projectId, { doe_type: t })
    applyServer(updated)
  }, [projectId, applyServer])

  const handleFactorsChange = useCallback(async (f: Factor[]) => {
    if (!projectId) return
    setPrefill(null)
    const updated = await updateStep5(projectId, { factors: f })
    setData(normalizeStep5(updated))
  }, [projectId])

  const handleResponsesChange = useCallback(async (r: DoeResponse[]) => {
    if (!projectId) return
    const updated = await updateStep5(projectId, { responses: r })
    applyServer(updated)
  }, [projectId, applyServer])

  const handleCasesChange = useCallback(async (c: DoECase[]) => {
    if (!projectId) return
    const updated = await updateStep5(projectId, { cases: c })
    applyServer(updated)
  }, [projectId, applyServer])

  const handleGenerate = useCallback(async () => {
    if (!projectId || !data?.doe_type) return
    setGenerating(true)
    try {
      await generateCases(projectId, data.doe_type, data.factors, data.responses,
        data.doe_type === 'mixture' ? mixtureTotal : undefined)
      const updated = await getStep5(projectId)
      setPrefill(null)  // 생성이 요인 목록을 저장한다
      setData(normalizeStep5(updated))
    } catch (e) {
      toast(apiErrorMessage(e, 'DoE 케이스를 만들지 못했습니다.'), 'warn', 8000)
    } finally {
      setGenerating(false)
    }
  }, [projectId, data, mixtureTotal])

  const handleUploadY = useCallback(async (file: File) => {
    if (!projectId) return
    setUploadingY(true)
    try {
      await uploadYValues(projectId, file)
      const updated = await getStep5(projectId)
      applyServer(updated)
    } finally {
      setUploadingY(false)
    }
  }, [projectId, applyServer])

  const handleAnalyze = useCallback(async () => {
    if (!projectId || !data?.doe_type) return
    setAnalyzing(true)
    try {
      await analyzeDoe(projectId, data.doe_type, data.cases)
      const updated = await getStep5(projectId)
      applyServer(updated)
    } catch (e) {
      toast(apiErrorMessage(e, 'DoE 분석을 하지 못했습니다.'), 'warn', 8000)
    } finally {
      setAnalyzing(false)
    }
  }, [projectId, data, applyServer])

  // 2026-09-23 - 이슈 #83: 요청 동안 진행 중 작업 잠금(#82)에 등록한다 — 다른 스텝·프로젝트 전환을 잠근다.
  // 중단 수단은 두지 않는다: 서버가 한 요청 안에서 계산·저장해 끊어도 저장되므로 Break 의 뜻과 어긋난다(검토 K3).
  useEffect(() => {
    if (!analyzing) return
    const id = crypto.randomUUID()
    useBusyStore.getState().begin({ id, step: 5, label: 'Step 5 DoE 분석' })
    return () => useBusyStore.getState().end(id)
  }, [analyzing])

  if (!data) return <p className="text-xs text-text-secondary">Loading...</p>

  return (
    <div>
      <DoeSelectSection selected={data.doe_type} onSelect={handleDoeTypeChange} />
      <VariableInputSection
        factors={data.factors}
        responses={data.responses}
        doeType={data.doe_type}
        mixtureTotal={mixtureTotal}
        prefilledFromStep3={prefill !== null}
        onMixtureTotalChange={setMixtureTotal}
        onFactorsChange={handleFactorsChange}
        onResponsesChange={handleResponsesChange}
      />
      <CaseTableSection
        cases={data.cases}
        onCasesChange={handleCasesChange}
        onGenerate={handleGenerate}
        generating={generating}
        blockReason={generateBlockReason(data)}
        templateUrl={projectId ? downloadTemplateUrl(projectId) : null}
        onUploadY={handleUploadY}
        uploading={uploadingY}
      />
      <AnalysisSection
        analysisResult={data.analysis_result}
        graphData={data.graph_data}
        onAnalyze={handleAnalyze}
        analyzing={analyzing}
      />
    </div>
  )
}
